import {
  useQuery,
  useSuspenseQuery,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import {
  getVisitByVisitId,
  postCreateVisit,
  patchUpdateVisitStatus,
  patchUpdateVisit,
  getVisits,
  deleteVisit,
  uploadPhoto,
  getPhotosByVisitId,
  deletePhoto,
  Photo,
} from './api';
import {
  type VisitQueryDto,
  type CreateVisitType,
  type ClientVisitSummaryDto,
  type VisitsByDateQueryParams,
  type VisitDetail,
  getVisitsByDateQueryKey,
  VisitForm,
} from './entity';
import { getClientVisits } from '../client/api';
import { queryClient } from '../../shared/queries';
import { PersistentFiltersType, STORAGE_KEY } from '@shared/entity';
import { mapVisitDetail, mapSingleVisitDetail } from './mappers';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

export const useSuspenseClientVisitsQuery = (clientId: string | undefined, isEnabled?: boolean) => {
  return useSuspenseQuery<ClientVisitSummaryDto[], Error, VisitDetail[]>({
    queryKey: [clientId, 'visits'],
    queryFn: () => {
      if (!clientId) {
        throw Error('Chybí ID klienta.');
      }

      return getClientVisits(clientId);
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    select: (data): VisitDetail[] => mapVisitDetail(data),
  });
};

export const useDeleteVisitMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('visits.mutation');

  return useMutation<string, Error, string | undefined>({
    mutationFn: async (visitId: string | undefined) => {
      if (!visitId) {
        throw new Error('ID návštěvy je nutné k jejímu smazání.');
      }

      await deleteVisit(visitId);

      return visitId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['receivedPayments'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackBarMessage({ text: t('deleteSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message || t('deleteError'), type: 'error' });
    },
  });
};

export const getVisitByIdQueryKey = (visitId: string | undefined) => ['visit', visitId];

export const useVisitQuery = (
  visitId: string | undefined,
  enabled = true,
  refetchOnMount = false,
) => {
  return useQuery<VisitQueryDto, Error, VisitDetail>({
    queryKey: getVisitByIdQueryKey(visitId),
    queryFn: async () => {
      if (!visitId) {
        throw new Error('ID návštěvy je povinné.');
      }

      return getVisitByVisitId(visitId).then(data => ({
        ...data,
        paidPrice: Number(data.paidPrice),
        deposit: Number(data.deposit),
      }));
    },
    enabled,
    refetchOnMount,
    select: mapSingleVisitDetail,
  });
};

export const useCreateVisitMutation = (
  options?: UseMutationOptions<CreateVisitType, Error, CreateVisitType>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('visits.mutation');

  return useMutation<CreateVisitType, Error, CreateVisitType>({
    mutationFn: (visitData: CreateVisitType) => postCreateVisit(visitData),
    onSuccess: (visitData, variables, context, meta) => {
      options?.onSuccess?.(visitData, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      queryClient.invalidateQueries({ queryKey: ['client', visitData.clientId] });
      addSnackBarMessage({ text: t('createSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message || t('createError'), type: 'error' });
    },
  });
};

export const useVisitStatusMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('visits.mutation');

  return useMutation<
    { visitId?: string; status: boolean },
    Error,
    { visitId?: string; status: boolean } | undefined
  >({
    mutationFn: async data => {
      if (!data || !data.visitId) {
        throw Error('Data pro úpravu návštěvy nejsou kompletní.');
      }

      await patchUpdateVisitStatus(data);
      return data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: getVisitByIdQueryKey(data.visitId) });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackBarMessage({ text: t('statusSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message || t('statusError'), type: 'error' });
    },
  });
};

export const useUpdateVisitMutation = (visitId: string | undefined, clientId?: string) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('visits.mutation');

  return useMutation<string, Error, VisitForm>({
    mutationFn: async (visitData: VisitForm) => {
      if (!visitId) {
        throw new Error('Id návštěvy je nutné k její úpravě.');
      }

      await patchUpdateVisit(visitId, visitData);
      return visitId;
    },
    onSuccess: () => {
      const persistentFilters = localStorage.getItem(STORAGE_KEY);
      const { from, to }: PersistentFiltersType['visits']['dashBoardVisitOverView']['dates'] =
        persistentFilters ? JSON.parse(persistentFilters).visits.dashBoardVisitOverView.dates : '';

      queryClient.invalidateQueries({ queryKey: ['visitPaymentQr', visitId] });
      queryClient.invalidateQueries({ queryKey: getVisitByIdQueryKey(visitId) });
      queryClient.invalidateQueries({
        queryKey: ['visits', dayjs(from).format('YYYY-MM-DD'), dayjs(to).format('YYYY-MM-DD')],
      });
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: [clientId, 'visits'] });
      }

      addSnackBarMessage({ text: t('updateSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message || t('updateError'), type: 'error' });
    },
  });
};

export const getVisitsQuery = (params: VisitsByDateQueryParams) => {
  return {
    queryKey: getVisitsByDateQueryKey(params),
    queryFn: () => getVisits(params.query, params.date),
    staleTime: 0,
  };
};

export const useVisitsQuery = (params: VisitsByDateQueryParams) => {
  return useQuery<ClientVisitSummaryDto[], Error, VisitDetail[]>({
    ...getVisitsQuery(params),
    refetchOnMount: true,
    select: data => mapVisitDetail(data),
  });
};
export const useSuspenseVisitsQuery = (params: VisitsByDateQueryParams) => {
  return useSuspenseQuery<ClientVisitSummaryDto[], Error, VisitDetail[]>({
    ...getVisitsQuery(params),
    refetchOnMount: true,
    select: data => mapVisitDetail(data),
  });
};

export const usePhotosQuery = (visitId: string) => {
  return useQuery<Photo[]>({
    queryKey: ['photos', visitId],
    queryFn: () => getPhotosByVisitId(visitId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useUploadPhotoMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('visits.mutation');

  return useMutation<Photo, Error, { visitId: string; imageData: string; description?: string }>({
    mutationFn: ({ visitId, imageData, description }) =>
      uploadPhoto(visitId, imageData, description),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photos', variables.visitId] });
      addSnackBarMessage({ text: t('uploadPhotoSuccess'), type: 'success' });
    },
    onError: error => {
      console.error('Error uploading photo:', error);
      addSnackBarMessage({ text: t('uploadPhotoError'), type: 'error' });
    },
  });
};

export const useDeletePhotoMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('visits.mutation');

  return useMutation<void, Error, { photoId: string; photoUrl: string; visitId: string }>({
    mutationFn: ({ photoId, photoUrl }) => deletePhoto(photoId, photoUrl),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photos', variables.visitId] });
      addSnackBarMessage({ text: t('deletePhotoSuccess'), type: 'success' });
    },
    onError: error => {
      console.error('Error deleting photo:', error);
      addSnackBarMessage({ text: t('deletePhotoError'), type: 'error' });
    },
  });
};
